import { useCallback, useRef, useState } from "react";

export interface ElementBounds {
  /** Rendered height in px. */
  height: number;
  /** Bottom edge in px from the top of the viewport. */
  bottom: number;
}

const UNMEASURED: ElementBounds = { height: 0, bottom: 0 };

/**
 * The live rendered height and on-screen bottom edge of an element, for layout
 * that has to sit against something whose size is not fixed (a sticky header
 * that wraps or drops a line on short viewports).
 *
 * Returns a callback ref to attach and the bounds - zeros until the element
 * mounts. Re-measured whenever the element resizes, and on window resize too,
 * since what sits above the element (an app header) can change height without
 * the element itself resizing; where `ResizeObserver` is unavailable only the
 * window resize re-measures. Listeners are torn down when the element unmounts
 * or the ref moves to another element. `bottom` is only meaningful for an
 * element that does not scroll with the page, such as a sticky header.
 */
export function useElementBounds<T extends HTMLElement>(): [
  ref: (element: T | null) => void,
  bounds: ElementBounds
] {
  const [bounds, setBounds] = useState<ElementBounds>(UNMEASURED);
  const stopTracking = useRef<(() => void) | null>(null);

  const ref = useCallback((element: T | null) => {
    stopTracking.current?.();
    stopTracking.current = null;
    if (!element) return;

    const measure = () => {
      const height = element.offsetHeight;
      const bottom = Math.round(element.getBoundingClientRect().bottom);
      setBounds((prev) =>
        prev.height === height && prev.bottom === bottom
          ? prev
          : { height, bottom }
      );
    };
    measure();

    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    observer?.observe(element);
    window.addEventListener("resize", measure);
    stopTracking.current = () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  return [ref, bounds];
}
